/**
 * Geofence + terms gate (#769), rendered around <App /> in main.tsx.
 *   1. Asks /geo.json for the viewer's country (GEO_TIMEOUT_MS), then resolves the mode from the shared policy.
 *      Unknown country = the policy's `unknown` mode ("browse_only", like Hyperliquid), logged with console.warn and
 *      retried GEO_RETRIES times, GEO_RETRY_DELAY_MS apart; a later real answer replaces the decision.
 *   2. Applies the network rule (owner decision D1): browse-only (including an unknown location) applies on mainnet
 *      only, so on testnet everyone may trade; blocked applies on both networks.
 *   3. blocked: only the blocked page renders (no app, no wallet).
 *   4. otherwise the app renders, with the terms-of-use dialog on top until the current terms version is accepted.
 */
import { useCallback, useEffect, useMemo, useState, type ReactNode } from 'react';
import { useEnvMaybe } from '../config/env';
import { useT } from '../i18n/useT';
import { GeoContext, type GeoContextValue } from './context';
import { GEO_RETRIES, GEO_RETRY_DELAY_MS, GEO_TIMEOUT_MS, loadAcceptanceRaw, lookupGeo, POLICY, saveAcceptanceRaw, TERMS } from './geo';
import { acceptanceRecord, needsTermsPrompt, networkAccess, resolveMode, type GeoDecision } from './policy';
import BlockedPage from './BlockedPage';
import TermsDialog from './TermsDialog';

export interface GeoGateProps {
  children: ReactNode;
  fetchImpl?: typeof fetch;
  timeoutMs?: number;
  /** Extra /geo.json attempts after an unknown answer (tests shorten these). */
  retries?: number;
  retryDelayMs?: number;
  now?: () => Date;
}

const systemNow = () => new Date();

export default function GeoGate({
  children,
  fetchImpl,
  timeoutMs = GEO_TIMEOUT_MS,
  retries = GEO_RETRIES,
  retryDelayMs = GEO_RETRY_DELAY_MS,
  now = systemNow,
}: GeoGateProps) {
  const [status, setStatus] = useState<'loading' | 'ready'>('loading');
  const [decision, setDecision] = useState<GeoDecision>(() => resolveMode(POLICY, { country: null }));
  const [source, setSource] = useState<'cloudfront' | 'unknown'>('unknown');
  const [termsAccepted, setTermsAccepted] = useState(() => !needsTermsPrompt(loadAcceptanceRaw(), TERMS.version));
  const [viewingTerms, setViewingTerms] = useState(false);
  // Outside an EnvProvider (unit tests) assume mainnet: the stricter network.
  const network = useEnvMaybe()?.env.name ?? 'mainnet';
  const t = useT();

  useEffect(() => {
    let live = true;
    let timer: ReturnType<typeof setTimeout> | undefined;
    const attempt = (n: number) => {
      void lookupGeo(fetchImpl, timeoutMs).then((r) => {
        if (!live) return;
        const d = resolveMode(POLICY, r.ok ? r.location : { country: null });
        const known = r.ok && d.country !== null;
        if (!known) {
          // Policy "unknown" (browse_only, shared/policy/README.md), logged so the miss is visible, then retried.
          const left = retries - n;
          console.warn(
            `[geo] viewer country unknown (${r.ok ? 'no country in /geo.json' : r.reason}); applying policy "unknown" = ${d.mode}` +
              (left > 0 ? `; retry ${n + 1}/${retries} in ${retryDelayMs} ms` : '; no retries left until reload'),
          );
          if (left > 0) timer = setTimeout(() => attempt(n + 1), retryDelayMs);
          // A failed retry keeps the decision already shown.
          if (n > 0) return;
        }
        setDecision(d);
        setSource(known ? 'cloudfront' : 'unknown');
        setStatus('ready');
      });
    };
    attempt(0);
    return () => {
      live = false;
      clearTimeout(timer);
    };
  }, [fetchImpl, timeoutMs, retries, retryDelayMs]);

  const acceptTerms = useCallback(() => {
    saveAcceptanceRaw(acceptanceRecord(TERMS.version, now()));
    setTermsAccepted(true);
  }, [now]);
  const showTerms = useCallback(() => setViewingTerms(true), []);

  // D7 hook (#769 part 2): a legalCheck second opinion would combine here with stricterMode(decision.mode, hlMode)
  // before the network rule; everything downstream reads the context value.
  const access = networkAccess(decision, network);
  const mode = access.mode;
  const value = useMemo<GeoContextValue>(
    () => ({
      status,
      decision,
      mode,
      source,
      canTrade: status === 'ready' && access.canTrade,
      pushAllowed: access.pushAllowed,
      termsAccepted,
      acceptTerms,
      showTerms,
    }),
    [status, decision, mode, source, access.canTrade, access.pushAllowed, termsAccepted, acceptTerms, showTerms],
  );

  if (status === 'loading') {
    return (
      <div className="flex min-h-screen items-center justify-center text-sm text-muted" data-testid="geo-loading">
        {t('geo.loading')}
      </div>
    );
  }

  if (mode === 'blocked') {
    return (
      <GeoContext.Provider value={value}>
        <BlockedPage decision={decision} />
      </GeoContext.Provider>
    );
  }

  return (
    <GeoContext.Provider value={value}>
      <div
        data-geo-mode={mode}
        data-geo-jurisdiction-mode={decision.mode}
        data-geo-network={network}
        data-geo-country={decision.country ?? ''}
        className="contents"
      >
        {children}
      </div>
      {!termsAccepted && <TermsDialog terms={TERMS} mode="gate" onAccept={acceptTerms} />}
      {termsAccepted && viewingTerms && <TermsDialog terms={TERMS} mode="view" onClose={() => setViewingTerms(false)} />}
    </GeoContext.Provider>
  );
}
